/**
 * Web-push infrastructure (FCM). This is plumbing for future product UI —
 * it exposes capability detection, permission, and device registration; it
 * never decides WHEN to prompt (product code must call
 * requestPushPermission from an explicit user gesture, never on load).
 *
 * Platform reality this module encodes:
 *  - Desktop Chrome/Edge/Firefox and Safari 16+ on macOS: push works in a
 *    normal tab, no install needed.
 *  - Android Chrome: works in a normal tab.
 *  - iOS/iPadOS 16.4+: Apple only grants the Push API to web apps ADDED TO
 *    THE HOME SCREEN. In a plain Safari tab, capability comes back
 *    'ios-requires-install' and the UI should show install guidance
 *    instead of a permission dialog that cannot work.
 * Detection is feature-based (`PushManager`/`Notification` presence,
 * display-mode) with the user agent only used to explain *why* push is
 * unavailable on iOS.
 */
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { app } from '../firebase/firebase';
import { isIOS, isAndroid } from '../utils/device';
import { registerPushEndpoint, unregisterPushEndpoint } from './apiService';

/**
 * Public VAPID key (Firebase Console → Cloud Messaging → Web Push
 * certificates). Read lazily — CRA's DefinePlugin inlines the env access
 * wherever it appears, and reading per call keeps tests able to vary it.
 */
function vapidPublicKey(): string {
  return process.env.REACT_APP_FIREBASE_VAPID_KEY || '';
}

/**
 * Remembered so logout can tell the backend which endpoint to deactivate
 * without asking FCM again. The raw token also lives inside Firebase's own
 * IndexedDB; this copy is only a convenience pointer for cleanup.
 */
const PUSH_TOKEN_STORAGE_KEY = 'bb_push_token';

/** Fired on window whenever a push arrives while the app is in the foreground. */
export const PUSH_MESSAGE_EVENT = 'bb:push-message';

export type PushCapabilityReason =
  | 'ok'
  | 'ios-requires-install'
  | 'unsupported-browser'
  | 'not-configured'
  | 'permission-denied';

export interface PushCapability {
  /** True when a permission request / registration could succeed here. */
  supported: boolean;
  /** Why not (or 'ok'). 'ios-requires-install' → show Add-to-Home-Screen UI. */
  reason: PushCapabilityReason;
  /** Current Notification.permission ('default' when never asked). */
  permission: NotificationPermission | 'unsupported';
  /** Running as an installed app (Home Screen / desktop install)? */
  standalone: boolean;
}

/** Installed-PWA detection: display-mode media query + legacy iOS flag. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari's pre-standard flag, still the reliable signal there.
  return (window.navigator as any).standalone === true;
}

function hasPushApis(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPushCapability(): PushCapability {
  const standalone = isStandalone();
  if (!hasPushApis()) {
    // On iOS the APIs are absent in a plain tab but appear once the site
    // runs as an installed web app — tell the UI to guide the install.
    if (isIOS() && !standalone) {
      return { supported: false, reason: 'ios-requires-install', permission: 'unsupported', standalone };
    }
    return { supported: false, reason: 'unsupported-browser', permission: 'unsupported', standalone };
  }
  if (!vapidPublicKey()) {
    return { supported: false, reason: 'not-configured', permission: Notification.permission, standalone };
  }
  if (Notification.permission === 'denied') {
    return { supported: false, reason: 'permission-denied', permission: 'denied', standalone };
  }
  return { supported: true, reason: 'ok', permission: Notification.permission, standalone };
}

/**
 * True when THIS browser already completed the enable flow for the current
 * login: permission granted and a token was registered with the backend.
 * Used by UI to decide whether to offer the enable prompt.
 */
export function isPushRegisteredLocally(): boolean {
  if (!hasPushApis() || Notification.permission !== 'granted') {
    return false;
  }
  try {
    return !!localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
  } catch {
    return false;
  }
}

/**
 * Ask the browser for notification permission. MUST be called from a user
 * gesture (button tap) — browsers punish unsolicited prompts and iOS hard-
 * requires a gesture. Never called automatically anywhere in this module.
 */
export async function requestPushPermission(): Promise<NotificationPermission | 'unsupported'> {
  const capability = getPushCapability();
  if (!capability.supported && capability.permission !== 'default') {
    return capability.permission;
  }
  try {
    return await Notification.requestPermission();
  } catch (error) {
    console.error('[Push] requestPermission error:', error);
    return 'unsupported';
  }
}

/** Best-effort device metadata for the endpoint list — display only. */
function describeDevice(): { platform: string; browser: string; device_name: string } {
  const ua = navigator.userAgent;
  const platform = isIOS() ? 'ios-pwa' : isAndroid() ? 'android' : 'desktop';
  let browser = 'unknown';
  if (/edg\//i.test(ua)) browser = 'Edge';
  else if (/firefox\//i.test(ua)) browser = 'Firefox';
  else if (/chrome\//i.test(ua)) browser = 'Chrome';
  else if (/safari\//i.test(ua)) browser = 'Safari';
  const device_name = `${browser} · ${platform}${isStandalone() ? ' (installed)' : ''}`;
  return { platform, browser, device_name };
}

async function getMessagingIfSupported() {
  if (!(await isSupported().catch(() => false))) return null;
  return getMessaging(app);
}

/**
 * Full enable flow for this browser: permission (if not yet granted) →
 * root-scope SW registration → FCM token → backend registration bound to
 * the authenticated user. Safe to call repeatedly — re-registration is an
 * upsert and doubles as token refresh/reconciliation (call it on app start
 * when permission is already granted).
 */
export async function registerPushDevice(): Promise<{ ok: boolean; reason?: string }> {
  const capability = getPushCapability();
  if (!capability.supported) {
    return { ok: false, reason: capability.reason };
  }
  if (Notification.permission !== 'granted') {
    const permission = await requestPushPermission();
    if (permission !== 'granted') {
      return { ok: false, reason: 'permission-' + permission };
    }
  }
  try {
    const messaging = await getMessagingIfSupported();
    if (!messaging) {
      return { ok: false, reason: 'unsupported-browser' };
    }
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const token = await getToken(messaging, {
      vapidKey: vapidPublicKey(),
      serviceWorkerRegistration: registration,
    });
    if (!token) {
      return { ok: false, reason: 'no-token' };
    }
    const registered = await registerPushEndpoint({ token, ...describeDevice() });
    if (!registered) {
      return { ok: false, reason: 'backend-rejected' };
    }
    try {
      localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);
    } catch {
      /* private mode — cleanup will rely on server-side invalidation */
    }
    wireForegroundMessages(messaging);
    return { ok: true };
  } catch (error) {
    console.error('[Push] registerPushDevice error:', error);
    return { ok: false, reason: 'error' };
  }
}

/**
 * Tear down this browser's push endpoint. Call BEFORE auth tokens are
 * cleared on logout (the backend call needs them). Both legs are
 * best-effort: deleteToken() kills the token at FCM itself, so even if the
 * backend call loses the race with a logout redirect, the orphaned row is
 * deactivated by the provider (UNREGISTERED) on the next send.
 */
export async function unregisterPushDevice(): Promise<void> {
  let storedToken: string | null = null;
  try {
    storedToken = localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  if (storedToken) {
    try {
      await unregisterPushEndpoint(storedToken);
    } catch (error) {
      console.error('[Push] backend unregister error:', error);
    }
  }
  try {
    const messaging = await getMessagingIfSupported();
    if (messaging) {
      await deleteToken(messaging);
    }
  } catch (error) {
    console.error('[Push] deleteToken error:', error);
  }
}

let foregroundWired = false;

/**
 * Foreground pushes don't hit the service worker — surface them as a DOM
 * event (same CustomEvent pattern as "bb:post-to-feed") so NotificationContext
 * can refresh the bell instantly without this module knowing about React.
 */
function wireForegroundMessages(messaging: NonNullable<Awaited<ReturnType<typeof getMessagingIfSupported>>>) {
  if (foregroundWired) return;
  foregroundWired = true;
  onMessage(messaging, (payload) => {
    window.dispatchEvent(new CustomEvent(PUSH_MESSAGE_EVENT, { detail: payload?.data ?? {} }));
  });
}

/**
 * App-start reconciliation: if this browser already granted permission and
 * has a registered token, silently re-register to pick up token rotation
 * and reattach the foreground listener. No permission prompt ever.
 */
export async function reconcilePushRegistrationOnStartup(): Promise<void> {
  try {
    if (!hasPushApis() || Notification.permission !== 'granted') return;
    let hadToken = false;
    try {
      hadToken = !!localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
    } catch {
      return;
    }
    if (!hadToken) return;
    await registerPushDevice();
  } catch (error) {
    console.error('[Push] startup reconcile error:', error);
  }
}
